/* iter55g7 — Community Character Codex — public browse + import.
 *
 * Anyone (auth or not) can browse published characters. An authenticated
 * writer can one-click import an entry as a new character in one of their
 * projects.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Sparkles, Search, Loader2, Book, ArrowRight, ChevronLeft, User as UserIcon,
} from 'lucide-react';
import { codexApi, projectsApi } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { useAuth } from '@/lib/auth_context';

const ROLES = ['', 'Protagonist', 'Antagonist', 'Supporting', 'Foil', 'Mentor'];
const SORTS = [
  { key: 'newest',  label: 'Newest' },
  { key: 'popular', label: 'Most imported' },
  { key: 'name',    label: 'A → Z' },
];

export default function CodexBrowsePage() {
  const nav      = useNavigate();
  const { user } = useAuth();
  const authed   = !!user;
  const [entries, setEntries] = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [q,       setQ]       = useState('');
  const [role,    setRole]    = useState('');
  const [sort,    setSort]    = useState('newest');
  const [importOpen, setImportOpen] = useState(null);   // entry object or null

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    codexApi.list({ q: q || undefined, role: role || undefined, sort, limit: 60 })
      .then((d) => { if (!cancel) { setEntries(d.entries || []); setTotal(d.total || 0); } })
      .catch(() => { if (!cancel) { setEntries([]); setTotal(0); } })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [q, role, sort]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="codex-page">
      <header className="sticky top-0 z-10 border-b border-panel-border bg-panel/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <Link to="/" className="inline-flex items-center gap-2 text-[13px] text-muted-foreground hover:text-primary">
            <ChevronLeft className="h-3.5 w-3.5" />
            <span>Crown &amp; Rose</span>
          </Link>
          <h1 className="flex items-center gap-2 text-[15px] font-semibold tracking-tight">
            <Book className="h-4 w-4 text-primary" />
            The Character Codex
          </h1>
          <div className="w-24 text-right text-[10.5px] text-muted-foreground" data-testid="codex-total-count">
            {total.toLocaleString()} {total === 1 ? 'entry' : 'entries'}
          </div>
        </div>
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-2 px-4 pb-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search names, tags, or authors…"
              className="h-8 pl-7 text-[12.5px]"
              data-testid="codex-search-input"
            />
          </div>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            data-testid="codex-role-filter"
            className="h-8 rounded-sm border border-panel-border bg-background px-2 text-[12px]"
          >
            {ROLES.map((r) => <option key={r || 'any'} value={r} label={r || 'Any role'} />)}
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="codex-sort"
            className="h-8 rounded-sm border border-panel-border bg-background px-2 text-[12px]"
          >
            {SORTS.map((s) => <option key={s.key} value={s.key} label={`Sort: ${s.label}`} />)}
          </select>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6" data-testid="codex-grid-wrapper">
        {loading ? (
          <div className="grid place-items-center py-20 text-muted-foreground">
            <span className="inline-flex items-center gap-2 text-[13px]">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading the Codex…
            </span>
          </div>
        ) : entries.length === 0 ? (
          <div className="grid place-items-center rounded-md border border-dashed border-panel-border py-20 text-center text-muted-foreground">
            <Book className="h-8 w-8 mb-3 opacity-60" />
            <div className="text-[13px] font-semibold">No entries yet.</div>
            <div className="mt-1 max-w-md text-[11.5px]">
              Be the first to share a character — open the Character Bible in
              The Loom and click <b>Publish to Codex</b>.
            </div>
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="codex-grid">
            {entries.map((e) => (
              <li
                key={e.id}
                data-testid={`codex-card-${e.id}`}
                className="group flex flex-col rounded-md border border-panel-border bg-card p-3 transition-shadow hover:shadow-md"
              >
                <div className="mb-1 flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13.5px] font-semibold" data-testid={`codex-card-name-${e.id}`}>{e.name}</div>
                    <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{e.role}</div>
                  </div>
                  <span className="rounded-sm bg-primary/10 px-1.5 py-0.5 text-[9.5px] font-semibold text-primary" title="Times imported">
                    ↺ {e.uses_count || 0}
                  </span>
                </div>
                {e.summary && (
                  <p className="mb-2 line-clamp-3 text-[11.5px] text-muted-foreground">{e.summary}</p>
                )}
                <div className="mb-2 flex flex-wrap gap-1">
                  {(e.tags || []).slice(0, 4).map((t) => (
                    <span key={t} className="rounded-sm bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground">#{t}</span>
                  ))}
                </div>
                <div className="mt-auto flex items-center justify-between gap-2 pt-1">
                  <span className="inline-flex items-center gap-1 text-[10.5px] text-muted-foreground">
                    <UserIcon className="h-3 w-3" /> {e.author_name || 'anon'}
                  </span>
                  <Button
                    size="sm"
                    className="h-6 gap-1 px-2 text-[10.5px]"
                    onClick={() => authed ? setImportOpen(e) : nav('/login?next=/codex')}
                    data-testid={`codex-card-import-${e.id}`}
                  >
                    <Sparkles className="h-3 w-3" /> Import
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>

      {importOpen && (
        <ImportDialog
          entry={importOpen}
          onClose={() => setImportOpen(null)}
          onImported={(newCharName) => {
            toast.success(`Imported "${newCharName}"`, {
              description: 'Open the Character Bible in The Loom to review and edit.',
              action: {
                label: 'Open The Loom',
                onClick: () => nav('/'),
              },
            });
            // Bump uses_count locally so the badge updates.
            setEntries((prev) => prev.map((r) =>
              r.id === importOpen.id ? { ...r, uses_count: (r.uses_count || 0) + 1 } : r
            ));
            setImportOpen(null);
          }}
        />
      )}
    </div>
  );
}

function ImportDialog({ entry, onClose, onImported }) {
  const [projects, setProjects] = useState([]);
  const [pid,      setPid]      = useState('');
  const [newName,  setNewName]  = useState(entry.name || '');
  const [loading,  setLoading]  = useState(false);
  const [listLoading, setListLoading] = useState(true);

  useEffect(() => {
    projectsApi.list()
      .then((r) => { setProjects(r || []); if (r?.[0]) setPid(r[0].id); })
      .catch(() => setProjects([]))
      .finally(() => setListLoading(false));
  }, []);

  const doImport = async () => {
    if (!pid) { toast.error('Select a project to import into.'); return; }
    setLoading(true);
    try {
      const r = await codexApi.importInto(entry.id, {
        project_id: pid,
        new_name: newName.trim() || undefined,
      });
      onImported(r.character.name);
    } catch (e) {
      toast.error('Could not import.', { description: e?.response?.data?.detail || '' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="codex-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" /> Import {entry.name}
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            A copy of this 5-tier bible will be added as a new character to the
            project you pick below. The original stays on the Codex.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Import into project</span>
            {listLoading ? (
              <span className="inline-flex items-center gap-1 text-[11.5px] text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading your projects…
              </span>
            ) : projects.length === 0 ? (
              <span className="rounded-sm border border-dashed border-panel-border p-2 text-[11.5px] text-muted-foreground">
                You don&apos;t have any projects yet — create one in The Loom first.
              </span>
            ) : (
              <select
                value={pid}
                onChange={(e) => setPid(e.target.value)}
                data-testid="codex-import-project-select"
                className="h-8 rounded-sm border border-panel-border bg-background px-2 text-[12px]"
              >
                {projects.map((p) => <option key={p.id} value={p.id} label={p.name} />)}
              </select>
            )}
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Character name (optional rename)</span>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={entry.name}
              maxLength={80}
              data-testid="codex-import-newname"
              className="h-7 text-[11.5px]"
            />
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            onClick={doImport}
            disabled={loading || !pid}
            data-testid="codex-import-confirm"
          >
            {loading
              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              : <ArrowRight className="h-3.5 w-3.5 mr-1" />}
            Import as new character
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
